import {
  Box,
  FormControlLabel,
  List,
  ListItem,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { COLORS } from '@/styles/tokens/colors';

import { FilterSearchInput } from './FilterSearchInput';
import { FilterOption, StaticFilterGroup } from './types';
import { filterOptions } from './utils';

interface StaticFilterGroupDisplayProps {
  group: StaticFilterGroup;
  value: string[]; // Only the selected values for this specific group
  onSelectAndClose: (optionValue: string) => void;
}

/**
 * Displays and handles filtering for a static list of filter options.
 */
export function StaticFilterGroupDisplay({
  group,
  value: selectedValues,
  onSelectAndClose,
}: StaticFilterGroupDisplayProps) {
  const { id, options, searchable = true, multi = true } = group;
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const filteredOptions = useMemo<FilterOption[]>(() => {
    if (!options || !Array.isArray(options)) {
      return [];
    }
    return filterOptions(options, debouncedSearchText);
  }, [options, debouncedSearchText]);

  useEffect(() => {
    return () => {
      setSearchText('');
      setDebouncedSearchText('');
    };
  }, [id]);

  return (
    <Stack
      width={240}
      maxHeight={400}
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {searchable && (
        <FilterSearchInput
          value={searchText}
          onChange={setSearchText}
          placeholder="Search..."
        />
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredOptions.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body-xs">No results found</Typography>
          </Box>
        )}

        {filteredOptions.length > 0 && (
          <List sx={{ px: 0.5, pb: 0.5, pt: 0 }}>
            {filteredOptions.map((option: FilterOption) => (
              <ListItem
                key={option.value}
                dense
                sx={{
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: COLORS.FUNCTIONAL.HOVER,
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectAndClose(option.value);
                }}
              >
                <FormControlLabel
                  control={
                    multi ? (
                      <StyledCheckbox
                        checked={selectedValues.includes(option.value)}
                        size="small"
                        sx={{ m: '9px', width: 20, height: 20 }}
                      />
                    ) : (
                      <Radio
                        checked={selectedValues.includes(option.value)}
                        size="small"
                        disableRipple
                      />
                    )
                  }
                  label={
                    <Typography
                      variant="body-xs"
                      component="label"
                      color={COLORS.TEXT.PRIMARY}
                      sx={{
                        width: '100%',
                      }}
                      noWrap
                      title={String(option.label)}
                    >
                      {option.label}
                    </Typography>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Stack>
  );
}
